// firebase/firestore.ts
import { initializeFirestore, doc, getDoc, collection, getDocs, query, where, connectFirestoreEmulator } from 'firebase/firestore';
import app, { auth } from './firebase';
import idSlugData from '../data/structures-id-and-slug.updated.json';
import { withRetry } from './connection';
import type { DetailedStructure } from '../types/structures';

// Configure Firestore to be resilient in dev environments (proxies/CORS)
// Use auto-detected long polling and force long polling for better reliability
const db = initializeFirestore(app, {
  experimentalAutoDetectLongPolling: true,
  // Add additional settings for better connection reliability
  experimentalForceLongPolling: false, // Don't combine with auto-detect
  // Ignore undefined properties to avoid serialization issues
  ignoreUndefinedProperties: true,
});

// Only connect to emulator in development if explicitly configured.
// To force prod Firestore even in dev, set VITE_FORCE_PROD_FIRESTORE=true
// or add ?firestore=prod (or ?noEmu=1) in the URL.
const forceProdFirestore = (() => {
  const FORCE_KEY = 'firestore:force-prod';
  const fromEnv = (import.meta as any)?.env?.VITE_FORCE_PROD_FIRESTORE === 'true';
  let fromUrl = false;
  try {
    const url = new URL(window.location.href);
    const param = url.searchParams.get('firestore');
    if (param === 'prod' || url.searchParams.get('noEmu') === '1') {
      fromUrl = true;
      try { sessionStorage.setItem(FORCE_KEY, '1'); } catch {}
    }
  } catch {
    // ignore URL parsing issues
  }
  if (fromEnv) {
    try { sessionStorage.setItem(FORCE_KEY, '1'); } catch {}
  }
  const fromSession = (() => { try { return sessionStorage.getItem(FORCE_KEY) === '1'; } catch { return false; } })();
  const fromLocal = (() => { try { return localStorage.getItem(FORCE_KEY) === '1'; } catch { return false; } })();
  return fromEnv || fromUrl || fromSession || fromLocal;
})();

// NEVER connect to emulator in production builds
if (import.meta.env.DEV && !forceProdFirestore && import.meta.env.VITE_USE_FIRESTORE_EMULATOR === 'true') {
  try {
    connectFirestoreEmulator(db, 'localhost', 8080);
  } catch (error) {
    // Emulator already connected or not available
    void error;
  }
}

export default db;

// Helper to check if user is authenticated
function isAuthenticated(): boolean {
  return auth.currentUser !== null;
}

// Helper to log authentication status for debugging
function logAuthStatus(operation: string): void {
  const user = auth.currentUser;
  if (user) {
    void operation;
  } else {
    void operation;
  }
}

export type UserProfile = {
  id: string;
  role?: string;
  portals?: string[];
  managed_structures?: string[];
};

export type StructurePrivate = DetailedStructure & { id: string };

export function getAccessCaps(claims: any, userProfile?: UserProfile | null) {
  const profile = userProfile || ({} as UserProfile);
  const role = typeof profile.role === 'string' ? profile.role.toLowerCase() : '';
  const portals = Array.isArray(profile.portals) ? profile.portals : [];
  const managed = Array.isArray(profile.managed_structures) ? profile.managed_structures : [];

  const isAdmin = claims?.admin === true;
  const isPortalManager = role === 'portal_manager' && portals.length > 0;
  const isStructureManager = managed.length > 0;

  return {
    isAdmin,
    isPortalManager,
    isStructureManager,
    portals,
  };
}

export async function getUserProfile(uid: string, email?: string | null): Promise<UserProfile | null> {
  logAuthStatus('getUserProfile');
  
  if (!isAuthenticated()) {
    return null;
  }

  return withRetry(async () => {
    const uidUsedForDoc = uid;
    const d = await getDoc(doc(db, 'users', uidUsedForDoc));
    if (d.exists()) {
      const raw: any = d.data() as any;
      const data = { id: d.id, ...(raw || {}) } as UserProfile;
      return data;
    }
    return null;
  }, null); // Return null as fallback
}

/**
 * Convenience helper to read only the fields the app needs to hydrate dashboards
 * after an Auth0 sign-in: role and managed_structures. Falls back to safe defaults.
 */
export async function getRoleAndManagedStructures(uid: string): Promise<{ role?: string; managed_structures: string[] }> {
  try {
    const snap = await getDoc(doc(db, 'users', uid));
    if (snap.exists()) {
      const data: any = snap.data() || {};
      const role = typeof data.role === 'string' ? data.role : undefined;
      const managed = Array.isArray(data.managed_structures) ? data.managed_structures.map(String) : [];
      return { role, managed_structures: managed };
    }
  } catch (_) {}
  return { role: undefined, managed_structures: [] };
}

/**
 * Ensure the Firestore users/<uid> document exists so downstream structure matching can rely
 * on unified profile fields (managed_structures, portals, roles). If the document is missing,
 * we create a minimal one derived from Auth0/Firebase ID token claims. If present, we merge in
 * any missing arrays from claims WITHOUT overwriting existing non-empty arrays.
 */
export async function ensureUserProfileExists(uid: string): Promise<void> {
  if (!isAuthenticated()) return; // nothing we can do if not authenticated yet
  try {
    const ref = doc(db, 'users', uid);
    const snap = await getDoc(ref);
    // Read current claims once (avoid multiple token refreshes)
    let claims: any = {};
    try {
      const tokenRes = await auth.currentUser!.getIdTokenResult(false);
      claims = tokenRes.claims || {};
    } catch {}

    // Extract arrays from possible Auth0 namespaces / un-namespaced fallbacks + Visit custom claims
    const ns = 'https://travelin.com/';
    const managedFromClaims: any = claims[ns + 'manager_structures'] || claims.manager_structures || claims.app_metadata?.manager_structures || claims.managed_structures;
    const portalsFromClaims: any = claims[ns + 'portals'] || claims.portals || claims.app_metadata?.portals || claims.visit_portals;
    const rolesFromClaims: any = claims[ns + 'roles'] || claims.roles || claims.authorization?.roles || claims.app_metadata?.roles || claims.visit_role;
    
    // Also capture Visit-specific scalar fields if present
    const userSource = claims['user_source'];
    const disabled = claims['disabled'];

    const managed_structures: string[] = Array.isArray(managedFromClaims) ? managedFromClaims.map(String) : [];
    const portals: string[] = Array.isArray(portalsFromClaims) ? portalsFromClaims.map(String) : [];
    const roles: string[] = Array.isArray(rolesFromClaims) ? rolesFromClaims.map(String) : [];

    if (!snap.exists()) {
      // Provision minimal profile doc
      const provisional = {
        created_at: Date.now(),
        managed_structures,
        portals,
        roles,
        // Visit-specific fields (if present)
        ...(typeof userSource === 'string' && { user_source: userSource }),
        ...(typeof disabled === 'boolean' && { disabled }),
        // Helpful for debugging matching logic later
        claim_snapshot: {
          managed_structures_count: managed_structures.length,
          portals_count: portals.length,
          roles_count: roles.length,
        }
      } as any;
      try {
        // Lazy import setDoc to keep top import list lean
        const { setDoc } = await import('firebase/firestore');
        await setDoc(ref, provisional, { merge: false });
      } catch (e) {
        void e;
      }
      return;
    }

    // Merge missing arrays only (do not overwrite existing non-empty arrays)
    try {
      const existing: any = snap.data() || {};
      const update: any = {};
      if ((!Array.isArray(existing.managed_structures) || existing.managed_structures.length === 0) && managed_structures.length) {
        update.managed_structures = managed_structures;
      }
      if ((!Array.isArray(existing.portals) || existing.portals.length === 0) && portals.length) {
        update.portals = portals;
      }
      if ((!Array.isArray(existing.roles) || existing.roles.length === 0) && roles.length) {
        update.roles = roles;
      }
      // Merge Visit-specific scalar fields if not already set
      if (!existing.user_source && typeof userSource === 'string') {
        update.user_source = userSource;
      }
      if (typeof existing.disabled !== 'boolean' && typeof disabled === 'boolean') {
        update.disabled = disabled;
      }
      if (Object.keys(update).length) {
        const { setDoc } = await import('firebase/firestore');
        await setDoc(ref, update, { merge: true });
      }
    } catch (mergeErr) {
      void mergeErr;
    }
  } catch (err) {
    // Non-fatal: swallow errors so auth flow continues
    void err;
  }
}

export async function getStructuresForUser(uid: string, email?: string | null) {
  try {
    // Ensure profile exists BEFORE fetching so a newly provisioned doc is available
    await ensureUserProfileExists(uid);
    const profile = await getUserProfile(uid, email);
    if (!profile) return [];
    const p: any = profile;
    const managed = p.managed_structures || p.managedStructures || p.managed || [];
    return managed as string[]; // ids or slugs depending on your data
  } catch (err) {
    // swallow or handle as needed; avoid noisy logs
    return [];
  }
}

/** Load {id,name,type,slug} authority list from public file */
export type IdSlugItem = { id: string; name: string; type?: string; slug: string };
export async function loadIdSlugList(): Promise<IdSlugItem[]> {
  const res = await fetch('/structures-id-and-slug.updated.json');
  if (!res.ok) throw new Error('Failed to load structures-id-and-slug.updated.json');
  const json = await res.json();
  const items = Array.isArray(json?.items) ? json.items : [];
  return items
    .filter((it: any) => it?.id && it?.slug)
    .map((it: any) => ({
      id: String(it.id),
      name: String(it.name ?? it.id),
      type: it.type ?? '',
      slug: String(it.slug),
    }));
}

/** Resolve a private structure document id from a public slug filename */
export function resolveStructureIdFromSlug(slug: string | null | undefined): string | null {
  try {
    if (!slug) return null;
    const normalize = (s: string) => String(s || '').trim().toLowerCase();
    const ensureJson = (s: string) => s.endsWith('.json') ? s : `${s}.json`;
    const target = normalize(ensureJson(String(slug)));
    const items: any[] = Array.isArray((idSlugData as any)?.items) ? (idSlugData as any).items : [];
    const match = items.find((it: any) => normalize(ensureJson(String(it?.slug))) === target);
    return match?.id ? String(match.id) : null;
  } catch {
    return null;
  }
}

/** Fetch a private structure document (collection: structures) using a public slug filename */
export async function getStructureBySlug(slug: string) {
  const id = resolveStructureIdFromSlug(slug);
  if (!id) return null;
  const docs = await getStructuresByIds([id]);
  return Array.isArray(docs) && docs.length ? docs[0] : null;
}

/**
 * Unified loader: fetch private structure (collection: structures) and its public twin (public-structures)
 * joined by id_backend/backend_id/structureId === private.id. Accepts either a private id or a slug-ish hint.
 */
export async function loadStructurePair(structureIdOrSlug?: string | null) {
  const normalize = (s: any) => String(s || '').trim().toLowerCase();
  const ensureJson = (s: string) => s.endsWith('.json') ? s : `${s}.json`;
  const stripJson = (s: string) => s.replace(/\.json$/i, '');

  const asId = structureIdOrSlug && typeof structureIdOrSlug === 'string'
    ? stripJson(structureIdOrSlug)
    : null;

  // Step 1: fetch private doc if we have an id candidate
  let privateDoc: any = null;
  if (asId) {
    const docs = await getStructuresByIds([asId]);
    if (Array.isArray(docs) && docs.length) {
      privateDoc = docs[0];
    }
  }

  // Step 2: fetch all public docs once (small collection expected)
  const publicDocs = await getAllPublicStructures();

  // Helper: find public doc by slug/id
  const matchPublicBySlug = (docs: any[], key: string | null) => {
    if (!key) return null;
    const target = normalize(ensureJson(key));
    return (docs || []).find((d: any) => {
      const ids = [
        d?.id,
        d?.slug,
        ensureJson(String(d?.id || '')),
        ensureJson(String(d?.slug || '')),
      ];
      return ids.some(val => val && normalize(val) === target);
    }) || null;
  };

  const matchByBackendId = (docs: any[], target: string | null) => {
    if (!target) return null;
    const normTarget = normalize(target);
    return (docs || []).find((d: any) => {
      const candidates = [
        d?.id_backend,
        d?.backend_id,
        d?.structureId,
        d?.idBackend,
        d?.backendId,
        d?.idbackend,
      ];
      return candidates.some(val => val && normalize(val) === normTarget);
    }) || null;
  };

  const matchPublicById = (docs: any[], target: string | null) => {
    if (!target) return null;
    const normTarget = normalize(target);
    return (docs || []).find((d: any) => {
      const ids = [
        d?.id,
        d?.slug,
        ensureJson(String(d?.id || '')),
        ensureJson(String(d?.slug || '')),
      ];
      return ids.some(val => val && normalize(val) === normTarget);
    }) || null;
  };

  // Step 3: match public by id_backend against private id (preferred)
  let publicDoc: any = null;
  const privateId = privateDoc?.id || privateDoc?._id || null;
  if (privateId) {
    publicDoc = matchByBackendId(publicDocs, privateId);
  }

  // Step 4: fallback match public by slug/id if no private or no match
  if (!publicDoc && asId) {
    const target = normalize(ensureJson(asId));

    // Try backend match using provided key as an id
    publicDoc = matchByBackendId(publicDocs, asId);

    // If still not found, try document id/slug match
    if (!publicDoc) {
      publicDoc = matchPublicBySlug(publicDocs, target);
    }
  }

  // Step 5a: if we have a private doc with id_public, try to fetch matching public doc by id
  if (!publicDoc && privateDoc?.id_public) {
    publicDoc = matchPublicById(publicDocs, privateDoc.id_public);
  }

  // Step 5: If we have a public doc but no private doc, try fetching private via id_backend from public
  if (publicDoc && !privateDoc) {
    const backendId = publicDoc.id_backend || publicDoc.backend_id || publicDoc.structureId || null;
    if (backendId) {
      const docs = await getStructuresByIds([String(backendId)]);
      if (Array.isArray(docs) && docs.length) {
        privateDoc = docs[0];
      }
    }
  }

  return { privateDoc, publicDoc };
}

/** Fetch structures documents in chunks of 10 by documentId() */
export async function getStructuresByIds(ids: string[]) {
  if (!ids.length) return [];
  // Log auth state at call time
  logAuthStatus('getStructuresByIds');
  if (!isAuthenticated()) {
    return [];
  }
  try {
    const tokenRes = await auth.currentUser!.getIdTokenResult(false);
    const claimsPreview = {
      admin: (tokenRes.claims as any)?.admin === true,
      portalManager: (tokenRes.claims as any)?.portalManager === true,
      portal: (tokenRes.claims as any)?.portal || null,
    };
    void claimsPreview;
  } catch (e) {
    void e;
  }

  const results: any[] = [];
  let refreshed = false;
  let permissionDeniedCount = 0;
  for (const id of ids) {
    try {
      const ref = doc(db, 'structures', id);
      const d = await getDoc(ref);
      if (d.exists()) results.push({ id: d.id, ...d.data() });
    } catch (e: any) {
      const code = e?.code || e?.name || 'unknown';
      const uid = auth.currentUser?.uid || 'anonymous';
      const message = e?.message || String(e);
      void code;
      void uid;
      void message;
      // If we hit permission-denied once, force a token refresh and retry this id once.
      if (!refreshed && (code === 'permission-denied' || String(e).toLowerCase().includes('permission'))) {
        try {
          await auth.currentUser!.getIdToken(true);
          refreshed = true;
          const ref = doc(db, 'structures', id);
          const d2 = await getDoc(ref);
          if (d2.exists()) {
            results.push({ id: d2.id, ...d2.data() });
            continue;
          }
          // Optional: if after refresh we still lack privileged claims, avoid hammering
          try {
            const tokenRes2 = await auth.currentUser!.getIdTokenResult(false);
            const hasPrivileged = (tokenRes2.claims as any)?.admin === true || (tokenRes2.claims as any)?.portalManager === true;
            if (!hasPrivileged) {
              break;
            }
          } catch {}
        } catch (e2) {
          void e2;
        }
      }
      if (code === 'permission-denied' || String(e).toLowerCase().includes('permission')) {
        permissionDeniedCount += 1;
        if (permissionDeniedCount >= 3) {
          break;
        }
      }
      // ignore individual get errors; continue
    }
  }

  return results;
}

/** Fetch structures by their 'uid' field value (chunked IN queries of 10) */
export async function getStructuresByUids(uids: string[]) {
  if (!uids.length) return [];
  const results: any[] = [];
  const colRef = collection(db, 'structures');
  const CHUNK = 10;
  for (let i = 0; i < uids.length; i += CHUNK) {
    const chunk = uids.slice(i, i + CHUNK);
    try {
      const q = query(colRef, where('uid', 'in', chunk));
      const snap = await getDocs(q);
      snap.forEach((d) => results.push({ id: d.id, ...d.data() }));
    } catch (e) {
      // swallow chunk errors and continue to next
    }
  }
  return results;
}

/**
 * Fetch private structures by portal name from the structures collection only.
 * Uses query chunking (max 10 values) and dedupes by document id.
 */
export async function getPrivateStructuresForPortals(portals: string[]): Promise<StructurePrivate[]> {
  if (!Array.isArray(portals) || portals.length === 0) return [];

  const colRef = collection(db, 'structures');
  const CHUNK = 10;
  const chunks: string[][] = [];
  for (let i = 0; i < portals.length; i += CHUNK) {
    chunks.push(portals.slice(i, i + CHUNK));
  }

  const byId: Record<string, StructurePrivate> = {};

  const recordDocs = (snap: any) => {
    snap.forEach((d: any) => {
      byId[String(d.id)] = { id: d.id, ...(d.data() || {}) } as StructurePrivate;
    });
  };

  const isPermissionDenied = (e: any) => {
    const code = e?.code || e?.name || '';
    return code === 'permission-denied' || String(code).toLowerCase().includes('permission');
  };

  const shouldFallbackToNested = (e: any) => {
    const code = e?.code || e?.name || '';
    return code === 'failed-precondition' || code === 'invalid-argument';
  };

  const normalizePortalName = (name: string) => {
    return String(name || '')
      .toLowerCase()
      .replace(/[^a-z0-9]/g, '')
      .trim();
  };

  const extractPortalIdsFromProfile = (profile: UserProfile | null): string[] => {
    const rawPortals: any[] = Array.isArray(profile?.portals) ? (profile?.portals as any[]) : [];
    if (!rawPortals.length) return [];

    const desired = portals.map(normalizePortalName).filter(Boolean);
    const ids: string[] = [];

    for (const entry of rawPortals) {
      if (!entry || typeof entry !== 'object') continue;
      const portalName =
        entry.portalName || entry.portal || entry.name || entry.portal_name || entry.portalId || entry.portal_id || null;
      if (portalName && desired.length) {
        const normalized = normalizePortalName(String(portalName));
        if (!desired.includes(normalized)) continue;
      }
      const list =
        entry.structure_ids ||
        entry.structureIds ||
        entry.structures ||
        entry.ids ||
        entry.managed_structures ||
        entry.managedStructures ||
        [];
      if (Array.isArray(list)) {
        for (const id of list) {
          if (id !== null && id !== undefined) ids.push(String(id));
        }
      }
    }

    return Array.from(new Set(ids));
  };

  let queryError: any = null;
  for (const chunk of chunks) {
    try {
      const q = query(colRef, where('portal', 'in', chunk));
      const snap = await getDocs(q);
      recordDocs(snap);
    } catch (e: any) {
      if (isPermissionDenied(e)) {
        throw { code: 'permission-denied', error: e };
      }
      if (!shouldFallbackToNested(e)) {
        queryError = e;
        continue;
      }
      try {
        const q = query(colRef, where('structure.portal_administrators.portal_name', 'in', chunk));
        const snap = await getDocs(q);
        recordDocs(snap);
      } catch (nestedErr: any) {
        if (isPermissionDenied(nestedErr)) {
          throw { code: 'permission-denied', error: nestedErr };
        }
        queryError = nestedErr;
      }
    }
  }

  const queryResults = Object.values(byId);
  if (queryResults.length > 0) return queryResults;

  const uid = auth.currentUser?.uid || null;
  const profile = uid ? await getUserProfile(uid, auth.currentUser?.email || undefined) : null;
  const idsFromProfile = extractPortalIdsFromProfile(profile);
  if (!idsFromProfile.length) {
    console.warn('Missing portal structure ids in user profile; cannot load private structures by portal.');
    throw { code: 'missing-portal-structure-ids', result: [], error: queryError };
  }

  const docs = await getStructuresByIds(idsFromProfile);
  const dedup: Record<string, StructurePrivate> = {};
  for (const doc of Array.isArray(docs) ? docs : []) {
    if (!doc?.id) continue;
    dedup[String(doc.id)] = doc as StructurePrivate;
  }
  return Object.values(dedup);
}

/** Fetch ALL public structure documents from Firestore (collection: public-structures) */
export async function getAllPublicStructures() {
  return withRetry(async () => {
    const colRef = collection(db, 'public-structures');
    const snap = await getDocs(colRef);
    const out: any[] = [];
    snap.forEach((d) => {
      const data = d.data() || {};
      out.push({ id: d.id, slug: d.id, ...data });
    });
    return out;
  });
}

/**
 * DEPRECATED: Prefer getAllPublicStructures(). Kept for temporary compatibility.
 * Fetch a public structure document by slug (doc id) from Firestore collection: public-structures
 */
export async function getPublicStructureBySlug(slug: string) {
  try {
    const ref = doc(db, 'public-structures', slug);
    const snap = await getDoc(ref);
    if (snap.exists()) {
      const data = snap.data();
      return { id: snap.id, slug, ...(data || {}) } as any;
    }
  } catch {}
  return null;
}

/**
 * Fetch structures assigned to specific portals by querying public_structures collection
 * and then fetching the full structure data from structures collection
 */
export async function getStructuresForPortals(userPortals: string[]) {
  if (!userPortals.length) {
    return [];
  }

  return withRetry(async () => {
    // Capability gate: read claims to decide if we can use private portal path
    let claims: any = {};
    try {
      const tokenRes = await auth.currentUser?.getIdTokenResult(false);
      claims = tokenRes?.claims ?? {};
    } catch {}

    const getClaimPortals = (c: any): string[] => {
      try {
        const ns = 'https://travelin.com/';
        const portalsFromNs = c?.[ns + 'portals'];
        const direct = c?.portals;
        const scalar = c?.portal;
        const visit = c?.visit_portals;
        const list = Array.isArray(portalsFromNs) ? portalsFromNs
                  : Array.isArray(direct) ? direct
                  : Array.isArray(visit) ? visit
                  : scalar ? [scalar] : [];
        return list.filter((v: any) => typeof v === 'string' && v.trim()).map(String);
      } catch { return []; }
    };

    const normalizePortalName = (name: string) => {
      return String(name || '')
        .toLowerCase()
        .replace(/[^a-z0-9]/g, '')
        .replace(/travelin/g, '')
        .trim();
    };

    const canUsePortalPrivatePath = (c: any, incoming: string[]): boolean => {
      if (c?.portalManager !== true) return false;
      const claimPortals = getClaimPortals(c);
      if (!claimPortals.length || !incoming.length) return false;
      const normalizedClaims = claimPortals.map(normalizePortalName);
      const normalizedIncoming = incoming.map(normalizePortalName);
      return normalizedIncoming.some((u) => normalizedClaims.includes(u));
    };

    // Helper: Extract portal name from any document shape (nested or root)
    const extractPortalName = (obj: any): string | null => {
      try {
        if (obj?.structure?.portal_administrators?.portal_name) {
          const v = obj.structure.portal_administrators.portal_name;
          if (typeof v === 'string' && v.trim()) return v;
        }
      } catch {}
      const v2 = obj?.portal;
      return (typeof v2 === 'string' && v2.trim()) ? v2 : null;
    };

    // Local helper: map private id -> public slug filename from bundled mapping
    const getSlugForPrivateId = (id: string | undefined | null): string | null => {
      try {
        if (!id) return null;
        const items: any[] = Array.isArray((idSlugData as any)?.items) ? (idSlugData as any).items : [];
        const match = items.find((it: any) => String(it.id) === String(id));
        return match?.slug ? String(match.slug).replace(/^(?:\/)*/,'') : null;
      } catch { return null; }
    };

    // Step 1: Get all public structures
    const publicStructures = await getAllPublicStructures();

    // Step 2: Filter structures by portal name/admin using robust sources with fuzzy matching
    const matchingStructures: any[] = [];
    
    for (const structure of publicStructures) {
      try {
        // Primary source in data samples: structure.portal_administrators.portal_name
  let portalAdminValue: any = extractPortalName(structure);

        // Fallback: Look for portal admin name in modules[0].content (legacy path)
        if (!portalAdminValue) {
          const modules = structure.modules;
          if (Array.isArray(modules) && modules.length > 0) {
            const firstModule = modules[0];
            if (firstModule?.content) {
              const content = firstModule.content;
              const portalFieldNames = [
                'Nome admin portale:',
                'Nome admin portale',
                'nome admin portale:', 
                'nome admin portale', 
                'Nome Admin Portale:',
                'Nome Admin Portale',
                'NOME ADMIN PORTALE:',
                'NOME ADMIN PORTALE',
                'portal admin:',
                'portal admin',
                'Portal Admin:',
                'Portal Admin',
                'admin portale:',
                'admin portale',
                'Admin Portale:',
                'Admin Portale'
              ];
              for (const fieldName of portalFieldNames) {
                if (content[fieldName]) { portalAdminValue = content[fieldName]; break; }
              }
            }
          }
        }

        if (!portalAdminValue || typeof portalAdminValue !== 'string') continue;
        
        // Normalize the portal admin value and user portals for fuzzy matching
        
  const normalizedPortalAdmin = normalizePortalName(portalAdminValue);
        
        // Check if this structure matches any of the user's portals
        const matchedPortal = userPortals.find(userPortal => {
          const normalizedUserPortal = normalizePortalName(userPortal);
          
          // Direct match
          if (normalizedPortalAdmin === normalizedUserPortal) return true;
          
          // Partial match (one contains the other)
          if (normalizedPortalAdmin.includes(normalizedUserPortal) || 
              normalizedUserPortal.includes(normalizedPortalAdmin)) return true;
          
          // Handle common variations
          const visitBergamoVariants = ['visitbergamo', 'visitbergamotravelin', 'bergamo'];
          if (visitBergamoVariants.includes(normalizedPortalAdmin) && 
              visitBergamoVariants.includes(normalizedUserPortal)) return true;
          
          return false;
        });
        
        if (matchedPortal) {
          // Use the id_backend field from the public structure to point to the private structure
          const backendId = structure.id_backend || structure.backend_id || structure.structureId || structure.id;
          
          matchingStructures.push({
            public_structure_id: structure.id,       // ID of the public structure
            id_backend: backendId,                   // ID of the corresponding private structure
            portalName: matchedPortal,
            portalAdminValue
          });
        }
        
      } catch (error) {
        void error;
      }
    }
    
    // Early public-only return if claims do not allow private portal reads
    const allowPrivate = canUsePortalPrivatePath(claims, userPortals);
    if (!allowPrivate) {
      const publicOnly = matchingStructures.map(m => {
        const publicStructure = publicStructures.find(s => String(s.id) === String(m.public_structure_id));
        if (!publicStructure) return null;
        const filename = getSlugForPrivateId(m?.id_backend);
        const portalFromPublic = extractPortalName(publicStructure);
        return {
          id: publicStructure.id,
          id_backend: m.id_backend,
          backend_id: m.id_backend,
          slug: publicStructure.id,
          structure: publicStructure.structure || {},
          modules: publicStructure.modules || [],
          accessibility_scores: publicStructure.accessibility_scores || publicStructure.scores || undefined,
          _portalInfo: {
            portalName: portalFromPublic || m.portalName,
            portalAdminValue: m.portalAdminValue || portalFromPublic || m.portalName,
          },
          _publicStructureId: publicStructure.id,
          _filename: filename,
          _publicOnly: true,
          _source: 'public-structures',
          _reason: 'no-claims',
        } as any;
      }).filter(Boolean) as any[];
      return publicOnly;
    }

    // Step 3 (preferred path for portal managers): query private structures by portal(s)
    const colRef = collection(db, 'structures');
    const privateDocs: any[] = [];
    const claimPortals = getClaimPortals(claims);
    const chunks: string[][] = [];
    const CHUNK = 10;
    for (let i = 0; i < claimPortals.length; i += CHUNK) {
      chunks.push(claimPortals.slice(i, i + CHUNK));
    }
    // Query root 'portal' field
    for (const c of chunks) {
      try {
        const q = query(colRef, where('portal', 'in', c));
        const snap = await getDocs(q);
        snap.forEach((d) => privateDocs.push({ id: d.id, ...d.data() }));
      } catch {}
    }
    // Query nested 'structure.portal_administrators.portal_name'
    for (const c of chunks) {
      try {
        const q = query(colRef, where('structure.portal_administrators.portal_name', 'in', c));
        const snap = await getDocs(q);
        snap.forEach((d) => privateDocs.push({ id: d.id, ...d.data() }));
      } catch {}
    }
    // Dedupe private docs by id
    const mapById: Record<string, any> = {};
    for (const d of privateDocs) { mapById[String(d.id)] = d; }
    const uniquePrivate = Object.values(mapById);

    const gotPrivateIds = new Set(uniquePrivate.map((s: any) => String(s.id)));
    const enrichedPrivate = uniquePrivate.map((privateStructure) => {
      let matchInfo = matchingStructures.find(m => String(m.id_backend) === String(privateStructure.id));
      let publicStructure = matchInfo ? publicStructures.find(s => String(s.id) === String(matchInfo.public_structure_id))
                                      : publicStructures.find(s => String(s.id_backend) === String(privateStructure.id));
      if (!matchInfo && publicStructure) {
        matchInfo = {
          public_structure_id: publicStructure.id,
          id_backend: privateStructure.id,
          portalName: extractPortalName(privateStructure) || extractPortalName(publicStructure) || null,
          portalAdminValue: extractPortalName(privateStructure) || extractPortalName(publicStructure) || null,
        };
      }
      const filename = getSlugForPrivateId(privateStructure?.id);
      const portalFromPrivate = extractPortalName(privateStructure);
      const effectivePortal = portalFromPrivate || matchInfo?.portalName || null;
      return {
        ...privateStructure,
        id: privateStructure.id,
        _portalInfo: {
          portalName: effectivePortal,
          portalAdminValue: matchInfo?.portalAdminValue ?? effectivePortal,
        },
        _publicStructureId: matchInfo?.public_structure_id,
        _filename: filename,
        _publicOnly: false,
        _source: 'structures',
      } as any;
    });

    // Step 4: Public-only fallback for matches without private access
    const publicFallback = matchingStructures
      .filter(m => !gotPrivateIds.has(String(m.id_backend)))
      .map(m => {
        const publicStructure = publicStructures.find(s => String(s.id) === String(m.public_structure_id));
        if (!publicStructure) return null;
        const filename = getSlugForPrivateId(m?.id_backend);
        const portalFromPublic = extractPortalName(publicStructure);
        return {
          id: publicStructure.id,
          id_backend: m.id_backend,
          backend_id: m.id_backend,
          slug: publicStructure.id,
          structure: publicStructure.structure || {},
          modules: publicStructure.modules || [],
          accessibility_scores: publicStructure.accessibility_scores || publicStructure.scores || undefined,
          _portalInfo: {
            portalName: portalFromPublic || m.portalName,
            portalAdminValue: m.portalAdminValue || portalFromPublic || m.portalName,
          },
          _publicStructureId: publicStructure.id,
          _filename: filename,
          _publicOnly: true,
          _source: 'public-structures',
          _reason: 'not-found',
        } as any;
      })
      .filter(Boolean) as any[];

    const out = [...enrichedPrivate, ...publicFallback];
    return out;
    
  }, []); // Return empty array as fallback
}
